import { Box, List, ListIcon, ListItem, Text, VStack } from '@chakra-ui/layout';
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  useToast,
} from '@chakra-ui/react';
import LZString from 'lz-string';
import mixpanel from 'mixpanel-browser';
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router';

import { ReportEnrichmentTrackingEvents } from '@revelio/core';

import { CircleIcon } from '../../../../../shared/ui/icons';
import {
  parametersFromPageConfig,
  parameterUsesGlobalFilterMap,
  toReadableFilterName,
} from '../../../../CustomDash/components/Reports/Parameters/parameters';
import { useFilterContext } from '../../../../CustomDash/filters/contexts/FilterProvider';
import {
  type Filter,
  FilterId,
  type FilterSelectionsMap,
} from '../../../../CustomDash/filters/types/filters';
import { useReportRunMutations } from '../../../../CustomDash/hooks/useReportRunMutations';
import { buildPathFromPage } from '../../../../CustomDash/utils/routing-helpers';
import { DeliverableStepContainer } from '../../../../deliverables/deliverable-step-container';
import { useReportFilters } from '../../../hooks/useReportFilters';
import {
  getReportBuilderPath,
  ReportBuilderStep,
} from '../../../utils/report-builder-paths';
import { CustomReportBuilderContext } from '../../custom-report-builder-provider';
import styles from './custom-reports-confirmation.module.css';

export const CustomReportsConfirmation = () => {
  const navigate = useNavigate();
  const { filterState } = useFilterContext();
  const { selectedReportTemplate } = useContext(CustomReportBuilderContext);
  const [name, setName] = useState<string>('');
  const [nameError, setNameError] = useState<string | null>(null);
  const { handleCreateReportRun } = useReportRunMutations();
  const toast = useToast();
  const { additionalFilterIds, getReadableValue } = useReportFilters();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(true);

  if (!selectedReportTemplate) {
    return null;
  }
  const parameters = parametersFromPageConfig(selectedReportTemplate);

  const onSubmit = async () => {
    try {
      setIsLoading(true);
      if (!selectedReportTemplate) {
        throw new Error(
          'Selected report template is required to create a report run'
        );
      }

      const groupMap = filterState.groupMap;
      const templatePrimaryFilterId =
        selectedReportTemplate.tabs?.[0]?.tabContent?.primaryFilter?.id;

      const groupEntries = Array.from(groupMap.entries());

      const prunedAndOrderedGroupEntries: [string, FilterSelectionsMap][] =
        (() => {
          if (!templatePrimaryFilterId) return groupEntries;

          const filtered = groupEntries.filter(([groupKey]) => {
            if (
              groupKey === FilterId.COMPANY ||
              groupKey === FilterId.GEOGRAPHY ||
              groupKey === FilterId.ROLE
            ) {
              return groupKey === templatePrimaryFilterId;
            }
            return true;
          });

          const primaryIndex = filtered.findIndex(
            ([groupKey]) => groupKey === templatePrimaryFilterId
          );

          if (primaryIndex <= 0) return filtered;

          const primaryGroup = filtered[primaryIndex];
          if (!primaryGroup) return filtered;

          return [
            primaryGroup,
            ...filtered.slice(0, primaryIndex),
            ...filtered.slice(primaryIndex + 1),
          ];
        })();

      const groups = prunedAndOrderedGroupEntries.map(
        ([groupName, innerGroupMap]) => {
          const serializedInnerGroup = Array.from(innerGroupMap.entries());
          return [groupName, serializedInnerGroup] as [
            string,
            [FilterId, Filter][],
          ];
        }
      );

      const serializedGroups = LZString.compressToEncodedURIComponent(
        JSON.stringify(groups)
      );
      const basePath = buildPathFromPage(selectedReportTemplate);
      const to = serializedGroups
        ? `${basePath}?filterGroups=${serializedGroups}`
        : basePath;

      const paramDescriptions = parameters.map(
        (p) => `${p.name}: ${getReadableValue({ filterId: p.filterId })}`
      );
      const additionalDescriptions = additionalFilterIds.map(
        (fid) =>
          `${toReadableFilterName(fid as FilterId)}: ${getReadableValue({
            filterId: String(fid),
          })}`
      );
      const filtersDescription = [
        ...paramDescriptions,
        ...additionalDescriptions,
      ]
        .filter((s) => s.trim().length > 0)
        .join(' | ');
      await handleCreateReportRun.mutateAsync({
        reportId: selectedReportTemplate.id,
        name: name,
        owner: { id: '', username: '' },
        filters: serializedGroups ?? '',
        filtersDescription,
      });

      mixpanel.track(ReportEnrichmentTrackingEvents.REPORT_REQUESTED, {
        report_id: selectedReportTemplate.id,
        report_name: name,
        report_type: selectedReportTemplate.revelioPageId,
        page_type: 'report',
      });

      await navigate(to);
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : 'Failed to save report run';
      toast({
        title: 'Could not save report run',
        description: message,
        status: 'error',
        duration: 6000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DeliverableStepContainer
      id="report_builder_confirmation"
      title="Review your Report and Confirm"
      previousStepPath={getReportBuilderPath(ReportBuilderStep.ENTITY)}
      submitButtonContent="Submit"
      onSubmit={onSubmit}
      startOverPath={getReportBuilderPath(ReportBuilderStep.REPORT_TYPE)}
      isSubmitDisabled={isSubmitDisabled}
      isSubmitLoading={isLoading}
      isReportStep={true}
    >
      <Box p="8">
        <VStack align="stretch" spacing={4}>
          <Box>
            <Text
              color="text.primary"
              fontWeight="semibold"
              mb="2"
              className={styles.label}
            >
              Parameters
            </Text>
            <List spacing={2} ml={2}>
              {parameters.map((p) => (
                <ListItem key={p.parameterId}>
                  <ListIcon
                    as={CircleIcon}
                    color="green.500"
                    boxSize={2}
                    mb="5px"
                  />
                  <Text
                    as="span"
                    fontWeight={600}
                    color="text.primary"
                    mt="5px"
                    className={styles.label}
                  >
                    {p.name}:
                  </Text>{' '}
                  <Text as="span" color="text.primary" className={styles.value}>
                    {getReadableValue({
                      filterId: p.filterId,
                      ...(!parameterUsesGlobalFilterMap(p) && {
                        parameterKey: p.parameterId,
                      }),
                    })}
                  </Text>
                </ListItem>
              ))}
            </List>
          </Box>
          <FormControl id="report_name" isInvalid={!!nameError} mt="2">
            <FormLabel
              fontSize="sm"
              color="text.primary"
              className={styles.label}
            >
              Report Name
            </FormLabel>
            <Input
              value={name}
              onChange={(e) => {
                const cleaned = e.target.value.replace(/[^a-zA-Z0-9\s]/g, '');
                setName(cleaned);
                const isValid = cleaned.trim().length > 0;
                setNameError(isValid ? null : 'Report name is required.');
                setIsSubmitDisabled(!isValid);
              }}
              placeholder="Enter unique report name"
              width={350}
              className={styles.input}
            />
            <FormErrorMessage>{nameError}</FormErrorMessage>
          </FormControl>
        </VStack>
      </Box>
    </DeliverableStepContainer>
  );
};
